export const MASONRY_COMPOSE_VARIANT_COUNT = 6

export interface MasonryLayoutItem<T = string> {
  id: T
  width: number
  height: number
}

export interface MasonryPlacedItem<T = string> {
  id: T
  left: number
  top: number
  width: number
  height: number
  column: number
  presetIndex: number
}

interface MasonryAssignment<T> {
  item: MasonryLayoutItem<T>
  naturalHeightForColumn: number
  presetIndex: number
}

interface MasonryLayoutCandidate<T> {
  columns: number
  columnWidth: number
  heightPresets: number[]
  assignmentsByColumn: MasonryAssignment<T>[][]
  columnHeights: number[]
  downscale: number
  score: number
}

export const normalizeMasonryComposeVariantIndex = (value?: number): number => {
  if (typeof value !== 'number' || !Number.isFinite(value)) return 0
  const normalized = Math.trunc(value) % MASONRY_COMPOSE_VARIANT_COUNT
  return normalized >= 0
    ? normalized
    : normalized + MASONRY_COMPOSE_VARIANT_COUNT
}

export const getMasonryColumnCount = (itemCount: number): number => {
  if (itemCount <= 6) return 3
  if (itemCount <= 10) return 4
  if (itemCount <= 15) return 5
  return 6
}

export const orderMasonryLayoutItems = <T>(
  items: MasonryLayoutItem<T>[],
  variantIndex: number
): MasonryLayoutItem<T>[] => {
  if (items.length <= 1) return [...items]

  const normalized = normalizeMasonryComposeVariantIndex(variantIndex)
  if (normalized === 0) {
    return [...items]
  }

  if (normalized === 1) {
    return [...items].reverse()
  }

  if (normalized === 2) {
    return [...items].sort((a, b) => {
      const aRatio = a.height / Math.max(1, a.width)
      const bRatio = b.height / Math.max(1, b.width)
      return bRatio - aRatio
    })
  }

  if (normalized === 3) {
    return [...items].sort((a, b) => {
      const aRatio = a.height / Math.max(1, a.width)
      const bRatio = b.height / Math.max(1, b.width)
      return aRatio - bRatio
    })
  }

  if (normalized === 4) {
    const ordered = [...items]
    const result: MasonryLayoutItem<T>[] = []
    let start = 0
    let end = ordered.length - 1
    while (start <= end) {
      result.push(ordered[start]!)
      if (end !== start) {
        result.push(ordered[end]!)
      }
      start += 1
      end -= 1
    }
    return result
  }

  const midpoint = Math.floor(items.length / 2)
  return [...items.slice(midpoint), ...items.slice(0, midpoint)]
}

export const getMasonryPresetTieBreakerOrder = (
  variantIndex: number
): number[] => {
  const normalized = normalizeMasonryComposeVariantIndex(variantIndex) % 3
  if (normalized === 1) return [0, 1, 2]
  if (normalized === 2) return [2, 1, 0]
  return [1, 0, 2]
}

export const getMasonryHeightPresets = (columnWidth: number): number[] => {
  const short = Math.max(84, Math.round(columnWidth * 0.82))
  const medium = Math.max(110, Math.round(columnWidth * 1.08))
  const tall = Math.max(140, Math.round(columnWidth * 1.42))
  return [short, medium, tall]
}

export const pickMasonryPresetIndexForColumn = (
  naturalHeightForColumn: number,
  presets: number[],
  remainingHeight: number,
  previousPresetIndex: number | null,
  disallowedFirstPresetIndex: number | null,
  tieBreakerOrder: number[]
): number | null => {
  const candidates = presets
    .map((preset, index) => ({ preset, index }))
    .filter(({ preset }) => preset <= remainingHeight + 0.1)
    .sort((a, b) => {
      const deltaDifference =
        Math.abs(a.preset - naturalHeightForColumn) -
        Math.abs(b.preset - naturalHeightForColumn)

      if (deltaDifference !== 0) return deltaDifference

      return tieBreakerOrder.indexOf(a.index) - tieBreakerOrder.indexOf(b.index)
    })

  if (candidates.length === 0) return null

  for (const candidate of candidates) {
    if (
      previousPresetIndex !== null &&
      candidate.index === previousPresetIndex
    ) {
      continue
    }
    if (
      previousPresetIndex === null &&
      disallowedFirstPresetIndex !== null &&
      candidate.index === disallowedFirstPresetIndex
    ) {
      continue
    }
    return candidate.index
  }

  return candidates[0]?.index ?? null
}

export const normalizeMasonryColumnVariety = <T>(
  columnAssignments: MasonryAssignment<T>[],
  presets: number[]
): void => {
  if (columnAssignments.length < 2) return

  const presetSet = new Set(columnAssignments.map((item) => item.presetIndex))
  if (presetSet.size > 1) return

  const candidateIndex = Math.floor(columnAssignments.length / 2)
  const candidate = columnAssignments[candidateIndex]
  if (!candidate) return
  let bestAltPreset = candidate.presetIndex
  let bestAltDelta = Number.POSITIVE_INFINITY

  for (let presetIndex = 0; presetIndex < presets.length; presetIndex += 1) {
    if (presetIndex === candidate.presetIndex) continue
    const delta = Math.abs(
      (presets[presetIndex] ?? candidate.naturalHeightForColumn) -
        candidate.naturalHeightForColumn
    )
    if (delta < bestAltDelta) {
      bestAltDelta = delta
      bestAltPreset = presetIndex
    }
  }

  candidate.presetIndex = bestAltPreset
}

export const scoreMasonryLayoutCandidate = (
  columnHeights: number[],
  columns: number
): number => {
  const usedHeights = columnHeights.filter((height) => height > 0.5)
  if (usedHeights.length === 0) return Number.POSITIVE_INFINITY

  const emptyColumns = columns - usedHeights.length
  const trailingEmptyColumns = (() => {
    let trailing = 0
    for (let index = columnHeights.length - 1; index >= 0; index -= 1) {
      if (columnHeights[index]! > 0.5) break
      trailing += 1
    }
    return trailing
  })()
  const averageHeight =
    usedHeights.reduce((sum, height) => sum + height, 0) / usedHeights.length
  const heightVariance =
    usedHeights.reduce(
      (sum, height) => sum + (height - averageHeight) ** 2,
      0
    ) / usedHeights.length
  const heightStdDev = Math.sqrt(heightVariance)

  return emptyColumns * 10000 + trailingEmptyColumns * 5000 + heightStdDev
}

const buildMasonryLayoutCandidate = <T>(
  orderedItems: MasonryLayoutItem<T>[],
  columns: number,
  boardWidth: number,
  boardHeight: number,
  optimizationInset: number,
  composeVariantIndex: number
): MasonryLayoutCandidate<T> | null => {
  if (orderedItems.length === 0 || columns < 1) return null

  const usableWidth = Math.max(1, boardWidth - optimizationInset * 2)
  const usableHeight = Math.max(1, boardHeight - optimizationInset * 2)
  const columnWidth = usableWidth / Math.max(1, columns)
  const heightPresets = getMasonryHeightPresets(columnWidth)
  const tieBreakerOrder = getMasonryPresetTieBreakerOrder(composeVariantIndex)
  const minPreset = Math.min(...heightPresets)
  const assignmentsByColumn: MasonryAssignment<T>[][] = Array.from(
    { length: columns },
    () => []
  )
  const firstPresetByColumn: Array<number | null> = Array.from(
    { length: columns },
    () => null
  )
  let nextItemIndex = 0

  for (
    let column = 0;
    column < columns && nextItemIndex < orderedItems.length;
    column += 1
  ) {
    let columnHeight = 0
    let previousPresetIndex: number | null = null
    const disallowedTopPreset =
      column > 0 ? firstPresetByColumn[column - 1] : null

    while (nextItemIndex < orderedItems.length) {
      const item = orderedItems[nextItemIndex]!
      const naturalHeightForColumn =
        (columnWidth * item.height) / Math.max(1, item.width)
      const remainingHeight = usableHeight - columnHeight
      const presetIndex = pickMasonryPresetIndexForColumn(
        naturalHeightForColumn,
        heightPresets,
        remainingHeight,
        previousPresetIndex,
        disallowedTopPreset,
        tieBreakerOrder
      )
      if (presetIndex === null) break

      const presetHeight = heightPresets[presetIndex] ?? 0
      if (presetHeight > remainingHeight + 0.1) break

      assignmentsByColumn[column]!.push({
        item,
        naturalHeightForColumn,
        presetIndex,
      })
      if (firstPresetByColumn[column] === null) {
        firstPresetByColumn[column] = presetIndex
      }
      previousPresetIndex = presetIndex
      columnHeight += presetHeight
      nextItemIndex += 1

      if (usableHeight - columnHeight < minPreset - 0.1) break
    }
  }

  while (nextItemIndex < orderedItems.length) {
    const item = orderedItems[nextItemIndex]!
    const naturalHeightForColumn =
      (columnWidth * item.height) / Math.max(1, item.width)
    let targetColumn = 0
    let targetHeight = Number.POSITIVE_INFINITY
    for (let column = 0; column < columns; column += 1) {
      const columnHeight = assignmentsByColumn[column]!.reduce(
        (sum, assignment) =>
          sum + (heightPresets[assignment.presetIndex] ?? minPreset),
        0
      )
      if (columnHeight < targetHeight) {
        targetHeight = columnHeight
        targetColumn = column
      }
    }

    const columnAssignments = assignmentsByColumn[targetColumn]!
    const previousPresetIndex =
      columnAssignments.length > 0
        ? (columnAssignments[columnAssignments.length - 1]?.presetIndex ?? null)
        : null
    const presetIndex =
      pickMasonryPresetIndexForColumn(
        naturalHeightForColumn,
        heightPresets,
        usableHeight,
        previousPresetIndex,
        targetColumn > 0 && columnAssignments.length === 0
          ? firstPresetByColumn[targetColumn - 1]
          : null,
        tieBreakerOrder
      ) ?? 0

    columnAssignments.push({
      item,
      naturalHeightForColumn,
      presetIndex,
    })
    if (firstPresetByColumn[targetColumn] === null) {
      firstPresetByColumn[targetColumn] = presetIndex
    }
    nextItemIndex += 1
  }

  for (let column = 0; column < columns; column += 1) {
    normalizeMasonryColumnVariety(
      assignmentsByColumn[column] ?? [],
      heightPresets
    )
  }

  const columnHeights = assignmentsByColumn.map((columnAssignments) =>
    columnAssignments.reduce(
      (sum, assignment) => sum + (heightPresets[assignment.presetIndex] ?? 0),
      0
    )
  )
  const maxHeight = Math.max(1, ...columnHeights)
  const downscale = usableHeight / maxHeight

  return {
    columns,
    columnWidth,
    heightPresets,
    assignmentsByColumn,
    columnHeights,
    downscale,
    score: scoreMasonryLayoutCandidate(columnHeights, columns),
  }
}

export const buildBestMasonryLayout = <T>(
  orderedItems: MasonryLayoutItem<T>[],
  options: {
    boardWidth: number
    boardHeight: number
    optimizationInset: number
    composeVariantIndex?: number
  }
): MasonryPlacedItem<T>[] | null => {
  if (orderedItems.length === 0) return []

  const composeVariantIndex = normalizeMasonryComposeVariantIndex(
    options.composeVariantIndex
  )
  const defaultColumns = getMasonryColumnCount(orderedItems.length)
  const candidateColumns = Array.from(
    new Set(
      [defaultColumns, defaultColumns - 1].filter(
        (columnCount) => columnCount >= 3
      )
    )
  )

  if (composeVariantIndex % 2 === 1) {
    candidateColumns.reverse()
  }

  const candidateLayouts = candidateColumns
    .map((columns) =>
      buildMasonryLayoutCandidate(
        orderedItems,
        columns,
        options.boardWidth,
        options.boardHeight,
        options.optimizationInset,
        composeVariantIndex
      )
    )
    .filter(
      (candidate): candidate is MasonryLayoutCandidate<T> => candidate !== null
    )

  if (candidateLayouts.length === 0) return null

  const layout = candidateLayouts.reduce((best, candidate) =>
    candidate.score < best.score ? candidate : best
  )

  const placed: MasonryPlacedItem<T>[] = []
  for (let column = 0; column < layout.columns; column += 1) {
    const x = options.optimizationInset + column * layout.columnWidth
    let runningY = 0

    for (const assignment of layout.assignmentsByColumn[column] ?? []) {
      const frameWidth = layout.columnWidth
      const presetHeight =
        layout.heightPresets[assignment.presetIndex] ?? layout.columnWidth
      const frameHeight = presetHeight * layout.downscale

      placed.push({
        id: assignment.item.id,
        left: x,
        top: options.optimizationInset + runningY,
        width: frameWidth,
        height: frameHeight,
        column,
        presetIndex: assignment.presetIndex,
      })

      runningY += frameHeight
    }
  }

  return placed
}
